import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserRole } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Menu, X, LogOut, ChevronRight, User, Bell } from 'lucide-react';
import { Button } from '../buttons/Button';

export const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { notifications } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsOpen(false);
  };

  const getDashboardLink = () => {
    switch (user?.role) {
      case UserRole.STUDENT: return '/student/dashboard';
      case UserRole.AGENT: return '/agent/dashboard';
      case UserRole.LANDLORD: return '/landlord/dashboard';
      case UserRole.ADMIN: return '/admin/dashboard';
      default: return '/';
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex h-20 items-center justify-between">

            {/* Left: Logo */}
            <div className="flex items-center flex-shrink-0 z-10">
              <Link to="/" className="flex items-center gap-2 group">
                <img src="/logo.png" alt="Indigo Beans Rooms" className="h-16 w-auto" />
              </Link>
            </div>

            {/* Center: Navigation Menu (Desktop) - Absolutely Centered */}
            <div className="hidden lg:flex items-center space-x-8 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Link
                to="/"
                className={`text-sm font-semibold transition-colors ${isActive('/') ? 'text-[#2f5c96]' : 'text-gray-500 hover:text-[#2f5c96]'}`}
              >
                Home
              </Link>
              <Link
                to="/browse"
                className={`text-sm font-semibold transition-colors ${isActive('/browse') ? 'text-[#2f5c96]' : 'text-gray-500 hover:text-[#2f5c96]'}`}
              >
                Browse
              </Link>
              <Link
                to="/agents"
                className={`text-sm font-semibold transition-colors ${isActive('/agents') ? 'text-[#2f5c96]' : 'text-gray-500 hover:text-[#2f5c96]'}`}
              >
                Agents
              </Link>
              <Link
                to="/how-it-works"
                className={`text-sm font-semibold transition-colors ${isActive('/how-it-works') ? 'text-[#2f5c96]' : 'text-gray-500 hover:text-[#2f5c96]'}`}
              >
                How It Works
              </Link>
              <Link
                to="/pricing"
                className={`text-sm font-semibold transition-colors ${isActive('/pricing') ? 'text-[#2f5c96]' : 'text-gray-500 hover:text-[#2f5c96]'}`}
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className={`text-sm font-semibold transition-colors ${isActive('/contact') ? 'text-[#2f5c96]' : 'text-gray-500 hover:text-[#2f5c96]'}`}
              >
                Contact
              </Link>
            </div>

            {/* Right: Actions & Mobile Toggle */}
            <div className="flex items-center gap-4 z-10">
              {/* Desktop Auth */}
              <div className="hidden lg:flex items-center gap-4">
                {user ? (
                  <div className="flex items-center gap-4">
                    <Link to="/notifications" className="relative p-2 text-gray-500 hover:text-[#2f5c96] hover:bg-gray-100 rounded-full transition-all group" title="Notifications">
                      <Bell className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white border-2 border-white animate-pulse">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Link>
                    <Link to={getDashboardLink()}>
                      <Button variant="outline" className="!py-2 !px-4 !text-sm">Dashboard</Button>
                    </Link>
                    <button onClick={handleLogout} className="text-gray-500 hover:text-red-600 p-2 rounded-full hover:bg-gray-100 transition-colors" title="Log Out">
                      <LogOut className="w-5 h-5" />
                    </button>
                    {/* Profile Link */}
                    <Link to="/profile" className="block relative group">
                      <div className="w-10 h-10 rounded-full bg-[#2f5c96]/10 flex items-center justify-center text-[#2f5c96] font-bold border border-[#2f5c96]/20 overflow-hidden">
                        {user.avatar ? <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" /> : user.name.charAt(0)}
                      </div>
                      <div className="absolute top-full right-0 mt-2 w-max px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        View Profile
                      </div>
                    </Link>
                  </div>
                ) : (
                  <>
                    <Link to="/login" className="text-gray-600 hover:text-[#2f5c96] font-semibold text-sm transition-colors">Log in</Link>
                    <Link to="/register">
                      <Button className="!py-2 !px-5 !text-sm !rounded-full">Sign up</Button>
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile Menu Button */}
              <div className="flex items-center lg:hidden">
                <button
                  onClick={() => setIsOpen(true)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#2f5c96]"
                >
                  <span className="sr-only">Open main menu</span>
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </nav>

      {/* Mobile Slide-over Menu */}
      {isOpen && (
        <div className="relative z-50 lg:hidden" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel */}
          <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 shadow-xl transition-transform duration-300 ease-in-out transform translate-x-0">
            <div className="flex items-center justify-between mb-8">
              <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2" onClick={() => setIsOpen(false)}>
                <img src="/logo.png" alt="Indigo Beans Rooms" className="h-10 w-auto" />
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700 hover:bg-gray-100"
                onClick={() => setIsOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <X className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  <Link to="/" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">Home</Link>
                  <Link to="/browse" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">Browse</Link>
                  <Link to="/agents" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">Agents</Link>
                  <Link to="/how-it-works" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">How It Works</Link>
                  <Link to="/pricing" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">Pricing</Link>
                  <Link to="/contact" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">Contact & Support</Link>
                </div>

                <div className="py-6">
                  {user ? (
                    <div className="space-y-4">
                      {/* Mobile Profile Link */}
                      <Link to="/profile" className="flex items-center gap-3 px-3 py-2 bg-blue-50/50 rounded-lg hover:bg-blue-100 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-[#2f5c96] flex items-center justify-center text-white font-bold overflow-hidden">
                          {user.avatar ? <img src={user.avatar} alt="Profile" className="w-full h-full object-cover" /> : user.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{user.name}</p>
                          <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                        </div>
                      </Link>

                      <Link
                        to={getDashboardLink()}
                        className="w-full flex items-center justify-between rounded-lg bg-[#2f5c96] px-3.5 py-2.5 text-base font-semibold leading-7 text-white shadow-sm hover:bg-[#254a7a]"
                      >
                        Go to Dashboard <ChevronRight className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="-mx-3 block w-full text-left rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-red-600 hover:bg-red-50"
                      >
                        Log out
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Link to="/login" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                        Log in
                      </Link>
                      <Link to="/register" className="w-full flex justify-center rounded-lg bg-[#2f5c96] px-3 py-2.5 text-base font-semibold leading-7 text-white shadow-sm hover:bg-[#254a7a]">
                        Sign up free
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};